import React, { ReactElement, ReactNode } from 'react';

import styled from 'styled-components';

import { checkPermissionsViewVisibility } from '@/utils/permissions';
import { Button } from '@/widgets';
import theme from '@/utils/theme';
import { TEST_IDS } from '@/constants/testConstants';

import styles from './NoDataContainer.module.scss';

interface IStyledProps {
	theme: typeof theme;
	size: string;
}

interface INoDataProps {
	message: ReactNode;
	size: string;
	source: string;
	title: string;
	// eslint-disable-next-line @typescript-eslint/ban-ts-comment
	// @ts-ignore
	retry?: () => void;
	buttonText?: string;
	buttonDisabled?: boolean;
	className?: string;
	permission?: string;
}
const {
	DASHBOARD: { NO_DATA },
} = TEST_IDS;
const Container = styled.div`
	width: 100%;
	margin: 0 auto;
	text-align: center;
	font-style: normal;
	font-stretch: normal;
	letter-spacing: normal;
	padding-top: 50px;
`;

const decideSize = (props: IStyledProps) => {
	switch (props.size) {
		case 'small':
			return '15%';
		case 'medium':
			return '25%';
		case 'large':
			return '42%';
		default:
			return '30%';
	}
};
const ImageContainer = styled.img`
	height: ${(props: IStyledProps) => decideSize(props)};
	width: ${(props: IStyledProps) => decideSize(props)};
	display: block;
	margin: 0 auto;
`;

const decideMargin = (props: IStyledProps) => {
	switch (props.size) {
		case 'small':
			return '10px';
		case 'medium':
			return '15px';
		default:
			return '20px';
	}
};

const decideFont = ({ theme, size }: IStyledProps) => {
	switch (size) {
		case 'small':
			return theme.font.size.normal;
		case 'medium':
			return theme.font.size.big;
		default:
			return theme.font.size.large;
	}
};

const NoDataMessageContainer = styled.div`
	margin-top: ${(props: IStyledProps) => decideMargin(props)};
`;
const NoDataMessage = styled.p`
	font-size: ${(props: IStyledProps) => decideFont(props)};
	font-weight: ${(props: IStyledProps) => props.theme.font.weight.normal};
	line-height: 1.62;
	text-align: center;
	opacity: ${(props: IStyledProps) => props.theme.opacity.dark};
	color: ${(props: IStyledProps) => props.theme.colors.menu};
	font-family: ${(props: IStyledProps) => props.theme.font.family};
	width: 70%;
	margin: auto;
`;

const NoDataTitle = styled.p`
	font-size: 20px;
	font-weight: 500;
	line-height: 1.62;
	text-align: center;
	color: ${(props) => props.theme.colors.disabled};
	font-family: ${(props) => props.theme.font.family};
	margin-bottom: 0px;
`;

const NoDataContainer = (props: INoDataProps): ReactElement => {
	const {
		source,
		message,
		size,
		title,
		retry,
		buttonText = 'RETRY',
		buttonDisabled = false,
		className,
		permission = '',
	} = props;

	const _onClick = () => {
		if (retry) {
			retry();
		}
	};

	const _getButton = () => {
		if (permission) {
			return checkPermissionsViewVisibility(
				<Button
					disabled={buttonDisabled}
					onClick={_onClick}
					variant="primary"
					className={styles.buttonBorderRadius}
				>
					{buttonText}
				</Button>,
				permission,
			);
		}

		return (
			<Button
				disabled={buttonDisabled}
				onClick={_onClick}
				variant="primary"
				className={styles.buttonBorderRadius}
			>
				{buttonText}
			</Button>
		);
	};

	return (
		<div data-id={NO_DATA}>
			<Container className={className}>
				<ImageContainer src={source} size={size} />
				<NoDataMessageContainer size={size}>
					{title ? <NoDataTitle>{title}</NoDataTitle> : null}
					<NoDataMessage size={size}>{message}</NoDataMessage>
				</NoDataMessageContainer>
				{retry ? <div className={styles.button}>{_getButton()}</div> : null}
			</Container>
		</div>
	);
};

NoDataContainer.defaultProps = {
	source: '',
	message: '',
	size: '',
	title: '',
	retry: null,
};

export default NoDataContainer;
